import React, { cloneElement, createElement, isValidElement } from 'react'

import { cn } from './cn'
import { Checkbox, Radio } from './Checkbox'
import { Input } from './Input'
import { Label } from './Label'
import type { LabelProps } from './Label'

// DOM mirrors Semantic UI React's Form (collections/Form/Form.js), FormField (FormField.js),
// FormGroup (FormGroup.js), FormInput (FormInput.js) and FormRadio (FormRadio.js) for the props
// the app uses. Class words come first, in SUIR's order, as marker classes, joined outside
// cn() as Button does. The form's `attached fluid segment` class words come last.
//
// The styles are copied from Semantic UI 2.4.2 form.css for the variants the app uses. They are
// Tailwind arbitrary variants on the Form root. `&` stands for the root's marker class `form`,
// so '[&.ui_.field]' is Semantic's '.ui.form .field', with the same specificity. Rules for a
// lib/ui Input's inner 'input' repeat '.form' ('[&.ui.form_...]'), which is one class more than
// Semantic's, so that the Form still wins over input.css's ties. The loading overlay and spinner
// pseudo-elements are `ui-form-loading` in globals.css.
//
// Not implemented, because no call site uses them: `inverted`, `reply`, `unstackable`,
// `widths`, `grouped`, `width` on Form.Field, the `.wide`/`.N.fields` sizing combinations, the
// `.two.fields` and similar mobile rules, the `compact`/`icon` message variants, `corner`
// labels, `.select` and `.dropdown` inline rules, and textarea-only states other than the base.

export type FormSize = 'big' | 'large'

export type FormProps = Omit<
  React.FormHTMLAttributes<HTMLFormElement>,
  'onSubmit'
> & {
  children?: React.ReactNode
  className?: string
  error?: boolean
  loading?: boolean
  // SUIR calls onSubmit(event, props), and calls preventDefault on the event unless `action` is set.
  onSubmit?: (e: React.FormEvent<HTMLFormElement>, data: FormProps) => void
  size?: FormSize
  success?: boolean
  warning?: boolean
}

export type FormFieldError = boolean | string | LabelProps

// Props that are not listed here pass through to the control, as SUIR's `rest` does.
export type FormFieldProps = {
  [prop: string]: unknown
  children?: React.ReactNode
  className?: string
  control?: React.ElementType | 'input' | 'button' | 'select' | 'textarea'
  disabled?: boolean
  error?: FormFieldError
  id?: string
  inline?: boolean
  label?: React.ReactNode
  required?: boolean
  style?: React.CSSProperties
  type?: string
}

export type FormGroupProps = React.HTMLAttributes<HTMLDivElement> & {
  inline?: boolean
}

// The text inputs Semantic styles inside a form are written out in full in each class, because
// Tailwind only generates classes it finds as literal text. `:is()` gives every input type
// Semantic's (0,1,1) per entry.

// `.ui.form`, `.ui.form > p`, `.ui.large.form` and friends, and `.ui.loading.form`.
const FORM_ROOT = [
  '[&.ui]:relative [&.ui]:max-w-[100%] [&.ui]:[font-size:1rem]',
  '[&.ui>p]:[margin:1em_0em]',
  '[&.ui.large]:[font-size:1.14285714rem] [&.ui.big]:[font-size:1.28571429rem]',
  '[&.ui.loading]:[position:relative] [&.ui.loading]:[cursor:default] [&.ui.loading]:[pointer-events:none]',
].join(' ')

// `.ui.form .field` and `.ui.form .field > label`.
const FORM_FIELD = [
  '[&.ui_.field]:[clear:both] [&.ui_.field]:[margin:0em_0em_1em]',
  '[&.ui_.field:last-child]:[margin-bottom:0em] [&.ui_.fields:last-child_.field]:[margin-bottom:0em]',
  '[&.ui_.fields_.field]:[clear:both] [&.ui_.fields_.field]:[margin:0em]',
  '[&.ui_.field>label]:[display:block] [&.ui_.field>label]:[margin:0em_0em_0.28571429rem_0em]',
  '[&.ui_.field>label]:[color:rgba(0,0,0,0.87)] [&.ui_.field>label]:[font-size:0.92857143em]',
  '[&.ui_.field>label]:[font-weight:bold] [&.ui_.field>label]:[text-transform:none]',
].join(' ')

// `.ui.form textarea, .ui.form input:not([type]), .ui.form input[type=text]...` (base, width and
// vertical-align, then the field's padding, border and radius). Bumped by one class over
// Semantic's (0,3,1), so it beats input.css's `.ui.input.error > input` (0,3,1), as Semantic's
// later form.css does.
const FORM_TEXT_BASE = [
  '[&.ui.form_:is(input:not([type]),input[type=text],input[type=email],input[type=password],input[type=tel],input[type=number],textarea)]:[width:100%] [&.ui.form_:is(input:not([type]),input[type=text],input[type=email],input[type=password],input[type=tel],input[type=number],textarea)]:[vertical-align:top]',
  '[&.ui.form_:is(input:not([type]),input[type=text],input[type=email],input[type=password],input[type=tel],input[type=number],textarea)]:[font-family:var(--ui-font-sans)] [&.ui.form_:is(input:not([type]),input[type=text],input[type=email],input[type=password],input[type=tel],input[type=number],textarea)]:[margin:0em] [&.ui.form_:is(input:not([type]),input[type=text],input[type=email],input[type=password],input[type=tel],input[type=number],textarea)]:[outline:none]',
  '[&.ui.form_:is(input:not([type]),input[type=text],input[type=email],input[type=password],input[type=tel],input[type=number],textarea)]:[appearance:none] [&.ui.form_:is(input:not([type]),input[type=text],input[type=email],input[type=password],input[type=tel],input[type=number],textarea)]:[line-height:1.21428571em]',
  '[&.ui.form_:is(input:not([type]),input[type=text],input[type=email],input[type=password],input[type=tel],input[type=number],textarea)]:[padding:0.67857143em_1em] [&.ui.form_:is(input:not([type]),input[type=text],input[type=email],input[type=password],input[type=tel],input[type=number],textarea)]:[font-size:1em]',
  '[&.ui.form_:is(input:not([type]),input[type=text],input[type=email],input[type=password],input[type=tel],input[type=number],textarea)]:[background:#FFFFFF] [&.ui.form_:is(input:not([type]),input[type=text],input[type=email],input[type=password],input[type=tel],input[type=number],textarea)]:[border:1px_solid_rgba(34,36,38,0.15)]',
  '[&.ui.form_:is(input:not([type]),input[type=text],input[type=email],input[type=password],input[type=tel],input[type=number],textarea)]:[color:rgba(0,0,0,0.87)] [&.ui.form_:is(input:not([type]),input[type=text],input[type=email],input[type=password],input[type=tel],input[type=number],textarea)]:[border-radius:0.28571429rem]',
  '[&.ui.form_:is(input:not([type]),input[type=text],input[type=email],input[type=password],input[type=tel],input[type=number],textarea)]:[box-shadow:0em_0em_0em_0em_transparent_inset]',
  '[&.ui.form_:is(input:not([type]),input[type=text],input[type=email],input[type=password],input[type=tel],input[type=number],textarea)]:[transition:color_0.1s_ease,border-color_0.1s_ease]',
].join(' ')

// `.ui.form input[type=text]:focus`. Bumped by one class, as the base above is.
const FORM_TEXT_FOCUS = [
  '[&.ui.form_:is(input:not([type]),input[type=text],input[type=email],input[type=password],input[type=tel],input[type=number],textarea):focus]:[color:rgba(0,0,0,0.95)] [&.ui.form_:is(input:not([type]),input[type=text],input[type=email],input[type=password],input[type=tel],input[type=number],textarea):focus]:[border-color:#85B7D9]',
  '[&.ui.form_:is(input:not([type]),input[type=text],input[type=email],input[type=password],input[type=tel],input[type=number],textarea):focus]:[border-radius:0.28571429rem] [&.ui.form_:is(input:not([type]),input[type=text],input[type=email],input[type=password],input[type=tel],input[type=number],textarea):focus]:[background:#FFFFFF]',
  '[&.ui.form_:is(input:not([type]),input[type=text],input[type=email],input[type=password],input[type=tel],input[type=number],textarea):focus]:[box-shadow:0px_0em_0em_0em_rgba(34,36,38,0.35)_inset]',
].join(' ')

// `.ui.form ::-webkit-input-placeholder` and the focus and error variants.
const FORM_PLACEHOLDER = [
  '[&.ui_*]:placeholder:[color:rgba(191,191,191,0.87)]',
  '[&.ui_*:focus]:placeholder:[color:rgba(115,115,115,0.87)]',
  '[&.ui_.error_*]:placeholder:[color:#e7bdbc]',
  '[&.ui_.error_*:focus]:placeholder:[color:#da9796]',
].join(' ')

// `.ui.form .field.error` inputs and their focus (`.fields.error .field` is not used).
const FORM_TEXT_ERROR = [
  '[&.ui_.field.error_:is(input:not([type]),input[type=text],input[type=email],input[type=password],input[type=tel],input[type=number],textarea)]:[background:#FFF6F6] [&.ui_.field.error_:is(input:not([type]),input[type=text],input[type=email],input[type=password],input[type=tel],input[type=number],textarea)]:[border-color:#E0B4B4]',
  '[&.ui_.field.error_:is(input:not([type]),input[type=text],input[type=email],input[type=password],input[type=tel],input[type=number],textarea)]:[color:#9F3A38] [&.ui_.field.error_:is(input:not([type]),input[type=text],input[type=email],input[type=password],input[type=tel],input[type=number],textarea)]:[box-shadow:none]',
  '[&.ui_.field.error_:is(input:not([type]),input[type=text],input[type=email],input[type=password],input[type=tel],input[type=number],textarea):focus]:[background:#FFF6F6] [&.ui_.field.error_:is(input:not([type]),input[type=text],input[type=email],input[type=password],input[type=tel],input[type=number],textarea):focus]:[border-color:#E0B4B4]',
  '[&.ui_.field.error_:is(input:not([type]),input[type=text],input[type=email],input[type=password],input[type=tel],input[type=number],textarea):focus]:[color:#9F3A38] [&.ui_.field.error_:is(input:not([type]),input[type=text],input[type=email],input[type=password],input[type=tel],input[type=number],textarea):focus]:[box-shadow:none]',
  '[&.ui_.field.error_label]:[color:#9F3A38] [&.ui_.field.error_.input]:[color:#9F3A38]',
].join(' ')

// Chrome's autofill colours on fields, from form.css's `.ui.form .field.field input:-webkit-autofill`.
const FORM_AUTOFILL = [
  '[&.ui_.field.field_input:-webkit-autofill]:![box-shadow:0px_0px_0px_100px_#FFFFF0_inset]',
  '[&.ui_.field.field_input:-webkit-autofill]:![border-color:#E5DFA1]',
  '[&.ui_.field.field_input:-webkit-autofill:focus]:![box-shadow:0px_0px_0px_100px_#FFFFF0_inset]',
  '[&.ui_.field.field_input:-webkit-autofill:focus]:![border-color:#D5C315]',
  '[&.ui_.error.error_input:-webkit-autofill]:![box-shadow:0px_0px_0px_100px_#FFFAF0_inset]',
  '[&.ui_.error.error_input:-webkit-autofill]:![border-color:#E0B4B4]',
].join(' ')

// `.ui.form .field.error .ui.dropdown` and its menu. Semantic Dropdown keeps its own markup.
const FORM_DROPDOWN_ERROR = [
  '[&.ui_.field.error_.ui.dropdown]:[background:#FFF6F6] [&.ui_.field.error_.ui.dropdown]:[color:#9F3A38]',
  '[&.ui_.field.error_.ui.dropdown_.text]:[background:#FFF6F6] [&.ui_.field.error_.ui.dropdown_.text]:[color:#9F3A38]',
  '[&.ui_.field.error_.ui.dropdown_.item]:[background:#FFF6F6] [&.ui_.field.error_.ui.dropdown_.item]:[color:#9F3A38]',
  '[&.ui_.field.error_.ui.dropdown]:![border-color:#E0B4B4] [&.ui_.field.error_.ui.dropdown:hover]:![border-color:#E0B4B4]',
  '[&.ui_.field.error_.ui.dropdown:hover_.menu]:[border-color:#E0B4B4]',
  '[&.ui_.field.error_.ui.multiple.selection.dropdown>.label]:[background-color:#EACBCB]',
  '[&.ui_.field.error_.ui.multiple.selection.dropdown>.label]:[color:#9F3A38]',
  '[&.ui_.field.error_.ui.dropdown_.menu_.item:hover]:[background-color:#FBE7E7]',
  '[&.ui_.field.error_.ui.dropdown_.menu_.selected.item]:[background-color:#FBE7E7]',
  '[&.ui_.field.error_.ui.dropdown_.menu_.active.item]:![background-color:#FDCFCF]',
].join(' ')

// `.ui.form .field.error .checkbox:not(.toggle):not(.slider)`: the label, its box and check.
const FORM_CHECKBOX_ERROR = [
  '[&.ui_.field.error_.checkbox:not(.toggle):not(.slider)_label]:[color:#9F3A38]',
  '[&.ui_.field.error_.checkbox:not(.toggle):not(.slider)_label:before]:[background:#FFF6F6]',
  '[&.ui_.field.error_.checkbox:not(.toggle):not(.slider)_label:before]:[border-color:#E0B4B4]',
  '[&.ui_.field.error_.checkbox_label:after]:[color:#9F3A38]',
].join(' ')

// `.ui.form .disabled.field`, `.ui.form .field :disabled`, and `.field.disabled > label`.
const FORM_DISABLED = [
  '[&.ui_.disabled.field]:[pointer-events:none] [&.ui_.disabled.field]:[opacity:0.45]',
  '[&.ui_.field_:disabled]:[pointer-events:none] [&.ui_.field_:disabled]:[opacity:0.45]',
  '[&.ui_.field.disabled>label]:[opacity:0.45]',
  '[&.ui_.field.disabled_:disabled]:[opacity:1]',
].join(' ')

// `.ui.form .required.field > label:after` (the asterisk) and `.required.field > .checkbox:after`.
const FORM_REQUIRED = [
  "[&.ui_.required.field>label]:after:content-['*']",
  '[&.ui_.required.field>label]:after:[margin:-0.2em_0em_0em_0.2em]',
  '[&.ui_.required.field>label]:after:[color:#DB2828]',
  '[&.ui_.required.field>label]:after:[display:inline-block]',
  '[&.ui_.required.field>label]:after:[vertical-align:top]',
  "[&.ui_.required.field>.checkbox]:after:content-['*']",
  '[&.ui_.required.field>.checkbox]:after:[position:absolute]',
  '[&.ui_.required.field>.checkbox]:after:[top:0%]',
  '[&.ui_.required.field>.checkbox]:after:[left:100%]',
  '[&.ui_.required.field>.checkbox]:after:[margin:-0.2em_0em_0em_0.2em]',
  '[&.ui_.required.field>.checkbox]:after:[color:#DB2828]',
].join(' ')

// `.ui.form .field .prompt.label`: the error prompt. `!important` as in Semantic.
const FORM_PROMPT = [
  '[&.ui_.field_.prompt.label]:[white-space:normal]',
  '[&.ui_.field_.prompt.label]:![background:#FFFFFF]',
  '[&.ui_.field_.prompt.label]:![border:1px_solid_#E0B4B4]',
  '[&.ui_.field_.prompt.label]:![color:#9F3A38]',
].join(' ')

// `.ui.form .success.message` and friends are hidden; the state's message shows only when the
// Form has the same state word. `.ui.form .message:first-child` loses its top margin.
const FORM_MESSAGES = [
  '[&.ui_.success.message]:[display:none] [&.ui_.warning.message]:[display:none]',
  '[&.ui_.error.message]:[display:none]',
  '[&.ui.success_.success.message:not(:empty)]:[display:block]',
  '[&.ui.warning_.warning.message:not(:empty)]:[display:block]',
  '[&.ui.error_.error.message:not(:empty)]:[display:block]',
  '[&.ui_.message:first-child]:[margin-top:0px]',
].join(' ')

// `.ui.form .fields` (Form.Group): a flex row, with the field padding, on desktop. Below 767px
// the fields wrap and lose their bottom margin.
const FORM_GROUP = [
  '[&.ui_.fields]:[display:flex] [&.ui_.fields]:[flex-direction:row] [&.ui_.fields]:[margin:0em_-0.5em_1em]',
  '[&.ui_.fields>.field]:[flex:0_1_auto] [&.ui_.fields>.field]:[padding-left:0.5em]',
  '[&.ui_.fields>.field]:[padding-right:0.5em]',
  '[&.ui_.fields>.field:first-child]:[border-left:none] [&.ui_.fields>.field:first-child]:[box-shadow:none]',
  'max-[767px]:[&.ui_.fields]:[flex-wrap:wrap] max-[767px]:[&.ui_.fields]:[margin-bottom:0em]',
].join(' ')

// `.ui.form .inline.fields`: the inline group's margin, centring, field spacing and labels.
const FORM_INLINE = [
  '[&.ui_.inline.fields]:[margin:0em_0em_1em] [&.ui_.inline.fields]:[align-items:center]',
  '[&.ui_.inline.fields_.field]:[margin:0em] [&.ui_.inline.fields_.field]:[padding:0em_1em_0em_0em]',
  // Two selectors, not one `:is()`: `:is()` takes its most specific argument, which would let
  // the field-label margin beat `.inline.fields > label`'s own margin below. That label's top
  // and bottom margins come only from its `margin` shorthand, which wins in Semantic too.
  '[&.ui_.inline.fields>label]:[display:inline-block] [&.ui_.inline.fields_.field>label]:[display:inline-block]',
  '[&.ui_.inline.fields>label]:[width:auto] [&.ui_.inline.fields_.field>label]:[width:auto]',
  '[&.ui_.inline.fields_.field>label]:[margin-top:0em]',
  '[&.ui_.inline.fields_.field>label]:[margin-bottom:0em]',
  '[&.ui_.inline.fields>label]:[vertical-align:baseline] [&.ui_.inline.fields_.field>label]:[vertical-align:baseline]',
  '[&.ui_.inline.fields>label]:[font-size:0.92857143em] [&.ui_.inline.fields_.field>label]:[font-size:0.92857143em]',
  '[&.ui_.inline.fields>label]:[font-weight:bold] [&.ui_.inline.fields_.field>label]:[font-weight:bold]',
  '[&.ui_.inline.fields>label]:[color:rgba(0,0,0,0.87)] [&.ui_.inline.fields_.field>label]:[color:rgba(0,0,0,0.87)]',
  '[&.ui_.inline.fields>label]:[text-transform:none] [&.ui_.inline.fields_.field>label]:[text-transform:none]',
  '[&.ui_.inline.fields>label]:[margin:0.035714em_1em_0em_0em]',
  '[&.ui_.inline.fields_.field>:first-child]:[margin:0em_0.85714286em_0em_0em]',
  '[&.ui_.inline.fields_.field>:only-child]:[margin:0em]',
  '[&.ui_.inline.fields_.field>.selection.dropdown]:[width:auto]',
].join(' ')

// `.ui.form .field .ui.input` (the Form's Input wrapper is full width, its input auto).
const FORM_INPUT = [
  '[&.ui_.field_.ui.input]:[width:100%] [&.ui_.fields_.field_.ui.input]:[width:100%]',
  '[&.ui_.field_.ui.input_input]:[width:auto] [&.ui_.fields_.field_.ui.input_input]:[width:auto]',
].join(' ')

// `.ui.form .field > .selection.dropdown` (lib/ui Dropdown).
const FORM_DROPDOWN = [
  '[&.ui_.field>.selection.dropdown]:[width:100%]',
  '[&.ui_.field>.selection.dropdown>.dropdown.icon]:[float:right]',
].join(' ')

const FORM_CLASSES = [
  FORM_ROOT,
  FORM_FIELD,
  FORM_TEXT_BASE,
  FORM_TEXT_FOCUS,
  FORM_PLACEHOLDER,
  FORM_TEXT_ERROR,
  FORM_AUTOFILL,
  FORM_DROPDOWN_ERROR,
  FORM_CHECKBOX_ERROR,
  FORM_DISABLED,
  FORM_REQUIRED,
  FORM_PROMPT,
  FORM_MESSAGES,
  FORM_GROUP,
  FORM_INLINE,
  FORM_INPUT,
  FORM_DROPDOWN,
].join(' ')

// SUIR's Label.create for the error shorthand, with its defaults. A boolean `true` renders no
// prompt, as Label.create(true) returns null.
function errorPrompt(
  error: FormFieldError | undefined,
  id: string | undefined,
) {
  if (typeof error === 'string') {
    return (
      <Label
        prompt
        pointing="above"
        id={id ? `${id}-error-message` : undefined}
        role="alert"
        aria-atomic
        content={error}
      />
    )
  }
  if (typeof error === 'object' && error !== null) {
    return (
      <Label
        prompt
        pointing="above"
        id={id ? `${id}-error-message` : undefined}
        role="alert"
        aria-atomic
        {...error}
      />
    )
  }
  return null
}

// SUIR's createHTMLLabel with `htmlFor` = id.
function htmlLabel(label: React.ReactNode, htmlFor: string | undefined) {
  if (label === undefined || label === null || typeof label === 'boolean') {
    return null
  }
  if (isValidElement(label)) {
    return cloneElement(label as React.ReactElement<{ htmlFor?: string }>, {
      htmlFor,
      ...(label.props as object),
    })
  }
  return <label htmlFor={htmlFor}>{label}</label>
}

export function FormField(props: FormFieldProps) {
  const {
    children,
    className,
    control,
    disabled,
    error,
    id,
    inline,
    label,
    required,
    type,
    ...rest
  } = props

  // SUIR's class words: disabled, error, inline, required, then `field` and the className.
  const markers = [
    disabled && 'disabled',
    error && 'error',
    inline && 'inline',
    required && 'required',
    'field',
  ]
    .filter(Boolean)
    .join(' ')
  const classes = [markers, cn(className)].filter(Boolean).join(' ')

  const errorLabel = errorPrompt(error, id)

  if (control === undefined || control === null) {
    if (label === undefined || label === null) {
      return (
        <div {...rest} className={classes} id={id}>
          {children}
        </div>
      )
    }
    return (
      <div {...rest} className={classes} id={id}>
        {htmlLabel(label, undefined)}
        {errorLabel}
      </div>
    )
  }

  // SUIR's aria wiring: the control gets aria-describedby (with an id and an error) and
  // aria-invalid (with any error). The control's own props come after, as in SUIR.
  const ariaAttrs = {
    'aria-describedby': id && error ? `${id}-error-message` : null,
    'aria-invalid': error ? true : undefined,
  }
  const controlProps = {
    ...rest,
    children,
    disabled,
    required,
    type,
    id,
  }

  // A native checkbox or radio is wrapped in its label, as SUIR does.
  if (control === 'input' && (type === 'checkbox' || type === 'radio')) {
    return (
      <div className={classes}>
        <label>
          {createElement('input', { ...ariaAttrs, ...controlProps })} {label}
          {errorLabel}
        </label>
      </div>
    )
  }

  // Checkbox and Radio take the label themselves.
  if (control === Checkbox || control === Radio) {
    return (
      <div className={classes}>
        {createElement(control, { ...ariaAttrs, ...controlProps, label })}
        {errorLabel}
      </div>
    )
  }

  return (
    <div className={classes}>
      {htmlLabel(label, id)}
      {createElement(control as React.ElementType, {
        ...ariaAttrs,
        ...controlProps,
      })}
      {errorLabel}
    </div>
  )
}

// SUIR's FormInput: a Field whose control is Input unless one is passed.
export function FormInput({ control = Input, ...rest }: FormFieldProps) {
  return <FormField {...rest} control={control} />
}

// SUIR's FormRadio: a Field whose control is Radio unless one is passed.
export function FormRadio({ control = Radio, ...rest }: FormFieldProps) {
  return <FormField {...rest} control={control} />
}

export function FormGroup(props: FormGroupProps) {
  const { children, className, inline, ...rest } = props
  const classes = [inline && 'inline', 'fields', cn(className)]
    .filter(Boolean)
    .join(' ')
  return (
    <div {...rest} className={classes}>
      {children}
    </div>
  )
}

function FormRoot(props: FormProps) {
  const {
    action,
    children,
    className,
    error,
    loading,
    onSubmit,
    size,
    success,
    warning,
    ...rest
  } = props

  // SUIR's class words: size, error, loading, success, warning, then `form`.
  const markers = [
    'ui',
    size,
    error && 'error',
    loading && 'loading',
    success && 'success',
    warning && 'warning',
    'form',
    loading && 'ui-form-loading',
  ]
    .filter(Boolean)
    .join(' ')

  // SUIR calls preventDefault unless `action` is a string, then onSubmit(e, props).
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (typeof action !== 'string') e.preventDefault()
    onSubmit?.(e, props)
  }

  return (
    <form
      {...rest}
      action={action}
      className={`${markers} ${cn(FORM_CLASSES, className)}`}
      onSubmit={handleSubmit}
    >
      {children}
    </form>
  )
}

export function FormTextArea(props: FormFieldProps) {
  return <FormField {...props} control="textarea" />
}

export const Form = Object.assign(FormRoot, {
  Field: FormField,
  Group: FormGroup,
  Input: FormInput,
  Radio: FormRadio,
  TextArea: FormTextArea,
})
